// PlaybookForm — Create/Edit-Panel für ein Playbook.
// Felder: Titel, Auslöser-Stichwörter (eine Phrase pro Zeile), Antwort-Grundlage,
// Aktiv-Schalter. Plus PDF-Upload: liest den Text via pdf.js ins Antwort-Feld
// und archiviert die Datei (R2). Bei vorhandenem Attachment ein Download-Link.

import { useRef, useState } from 'react';
import { Check, FileUp, Loader2, Trash2, Download } from 'lucide-react';
import {
  uploadPlaybookPdf,
  openPlaybookPdf,
  attachmentIdFromKey,
  type AdminPlaybook,
  type PlaybookInput,
} from '@/lib/admin-playbooks-api';
import { extractPdfText } from '@/lib/pdf-extract';

interface PlaybookFormProps {
  accessToken: string | null;
  playbook: AdminPlaybook | null;
  onSave: (input: PlaybookInput) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => void;
}

function triggersToText(triggers: string[]): string {
  return triggers.join('\n');
}

function textToTriggers(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function PlaybookForm({
  accessToken,
  playbook,
  onSave,
  onCancel,
  onDelete,
}: PlaybookFormProps) {
  const isEdit = playbook !== null;
  const [title, setTitle] = useState(playbook?.title ?? '');
  const [triggersText, setTriggersText] = useState(
    playbook ? triggersToText(playbook.triggers) : '',
  );
  const [answer, setAnswer] = useState(playbook?.answer ?? '');
  const [active, setActive] = useState(playbook?.active ?? true);
  const [attachmentKey, setAttachmentKey] = useState<string | null>(
    playbook?.attachmentKey ?? null,
  );
  const [attachmentName, setAttachmentName] = useState<string | null>(
    playbook?.attachmentName ?? null,
  );

  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function onPickPdf(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Input zurücksetzen, damit dieselbe Datei erneut gewählt werden kann.
    e.target.value = '';
    if (!file || !accessToken) return;
    setPdfError(null);
    setPdfBusy(true);
    try {
      // 1. Text lokal auslesen → ins Antwort-Feld (Doc kann editieren).
      const text = await extractPdfText(file);
      setAnswer(text);
      // 2. Original archivieren (R2) → key/name für Speichern merken.
      const { key, name } = await uploadPlaybookPdf(accessToken, file);
      setAttachmentKey(key);
      setAttachmentName(name);
    } catch (err) {
      setPdfError(
        err instanceof Error ? err.message : 'PDF konnte nicht gelesen werden.',
      );
    } finally {
      setPdfBusy(false);
    }
  }

  async function onDownloadPdf() {
    if (!accessToken || !attachmentKey) return;
    setPdfError(null);
    try {
      await openPlaybookPdf(accessToken, attachmentIdFromKey(attachmentKey));
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Download fehlgeschlagen.');
    }
  }

  function removeAttachment() {
    setAttachmentKey(null);
    setAttachmentName(null);
  }

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    const input: PlaybookInput = {
      title: title.trim() || 'Ohne Titel',
      triggers: textToTriggers(triggersText),
      answer: answer.trim(),
      active,
      attachmentKey,
      attachmentName,
    };
    try {
      await onSave(input);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1800);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="glass-strong scroll-mt-4 rounded-2xl border border-white/5 border-t-2 border-t-amber-500/40 p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-[14px] font-semibold text-neutral-100">
          {isEdit ? 'Playbook bearbeiten' : 'Neues Playbook'}
        </h3>
        <span className="text-[11px] text-neutral-500">
          Änderungen greifen sofort für alle EVE-Modi
        </span>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Linke Spalte: Titel + Trigger */}
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-[12px] font-medium text-neutral-300">Titel</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. Coaching buchen"
              className="w-full rounded-xl border border-white/10 bg-black/40 px-3.5 py-2.5 text-[13px] text-neutral-100 outline-none transition placeholder:text-neutral-600 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[12px] font-medium text-neutral-300">
              Auslöser-Stichwörter{' '}
              <span className="font-normal text-neutral-500">— eine Phrase pro Zeile</span>
            </label>
            <textarea
              rows={5}
              value={triggersText}
              onChange={(e) => setTriggersText(e.target.value)}
              placeholder={'coaching buchen\ncoaching termin'}
              className="w-full resize-y rounded-xl border border-white/10 bg-black/40 px-3.5 py-2.5 font-mono text-[12px] leading-relaxed text-neutral-100 outline-none transition placeholder:text-neutral-600 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30"
            />
            <p className="mt-1.5 text-[11px] text-neutral-500">
              EVE gleicht Nutzerfragen unscharf gegen diese Phrasen ab.
            </p>
          </div>
        </div>

        {/* Rechte Spalte: Antwort-Grundlage + PDF */}
        <div className="flex flex-col">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <label className="block text-[12px] font-medium text-neutral-300">
              Verbindliche Antwort-Grundlage{' '}
              <span className="font-normal text-neutral-500">— EVE formuliert das in ihren Worten</span>
            </label>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={pdfBusy}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-neutral-300 transition hover:border-amber-500/40 hover:text-amber-300 disabled:opacity-50"
            >
              {pdfBusy ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <FileUp className="size-3.5" />
              )}
              {pdfBusy ? 'lese PDF …' : 'PDF hochladen'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={(e) => void onPickPdf(e)}
              className="hidden"
            />
          </div>

          <textarea
            rows={9}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Was EVE zu diesem Thema als Wahrheit vertreten soll …"
            className="w-full flex-1 resize-y rounded-xl border border-white/10 bg-black/40 px-3.5 py-3 text-[13px] leading-relaxed text-neutral-100 outline-none transition placeholder:text-neutral-600 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30"
          />

          {pdfError ? (
            <p className="mt-1.5 text-[11px] text-rose-300">{pdfError}</p>
          ) : (
            <p className="mt-1.5 text-[11px] text-neutral-500">
              Kein Skript zum Vorlesen — nur die Fakten, auf denen EVE ihre Antwort aufbaut. Großes
              PDF = langer Text = größerer Prompt; Playbooks sind für kompakte Antworten gedacht.
            </p>
          )}

          {attachmentKey ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] text-neutral-300">
              <FileUp className="size-3.5 shrink-0 text-amber-300" />
              <span className="min-w-0 flex-1 truncate">{attachmentName ?? 'Archiviertes PDF'}</span>
              <button
                type="button"
                onClick={() => void onDownloadPdf()}
                className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-0.5 text-neutral-300 transition hover:border-amber-500/40 hover:text-amber-300"
              >
                <Download className="size-3" />
                PDF herunterladen
              </button>
              <button
                type="button"
                onClick={removeAttachment}
                title="Anhang entfernen"
                className="inline-flex items-center rounded-md border border-white/10 px-1.5 py-0.5 text-neutral-400 transition hover:border-rose-500/40 hover:text-rose-300"
              >
                <Trash2 className="size-3" />
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Footer: Aktiv-Toggle + Aktionen */}
      <div className="mt-5 flex flex-col gap-4 border-t border-white/5 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <label className="flex cursor-pointer items-center gap-3 select-none">
          <button
            type="button"
            role="switch"
            aria-checked={active}
            onClick={() => setActive((v) => !v)}
            className={[
              'relative inline-flex h-[22px] w-10 shrink-0 rounded-full transition-colors',
              active ? 'bg-amber-400/85' : 'bg-white/10',
            ].join(' ')}
          >
            <span
              className={[
                'absolute top-0.5 left-0.5 size-[18px] rounded-full shadow transition-transform',
                active ? 'translate-x-[18px] bg-[#1a1300]' : 'bg-neutral-200',
              ].join(' ')}
            />
          </button>
          <span className="text-[13px] text-neutral-300">
            Aktiv{' '}
            <span className="text-neutral-500">
              {active ? '— EVE nutzt dieses Playbook' : '— pausiert, EVE ignoriert es'}
            </span>
          </span>
        </label>

        <div className="flex items-center gap-2">
          {saveError ? <span className="mr-1 text-[12px] text-rose-300">{saveError}</span> : null}
          {savedFlash ? (
            <span className="mr-1 inline-flex items-center gap-1 text-[12px] text-emerald-400/80">
              <Check className="size-3.5" />
              gespeichert
            </span>
          ) : null}
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-white/10 px-4 py-2 text-[13px] font-medium text-neutral-300 transition hover:bg-white/5"
          >
            Abbrechen
          </button>
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-[13px] font-medium text-rose-300 transition hover:bg-rose-500/20"
            >
              <Trash2 className="size-4" />
              Löschen
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl border border-amber-500/50 bg-amber-500/20 px-5 py-2 text-[13px] font-semibold text-amber-200 transition hover:bg-amber-500/30 disabled:opacity-60"
          >
            <Check className="size-4" />
            {saving ? 'Speichere…' : 'Speichern'}
          </button>
        </div>
      </div>
    </section>
  );
}
